import React, { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  InputLabel,
  Checkbox,
  Radio,
  RadioGroup,
  ListItemText,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BadgeIcon from '@mui/icons-material/Badge';
import WorkIcon from '@mui/icons-material/Work';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SearchIcon from '@mui/icons-material/Search';
import TextField from '@mui/material/TextField';

const typeDocOptions = [
  'CNI FR (Nouvelle)',
  'CNI FR (Ancienne)',
  'Passeport',
  'Titre de séjour',
  'CNI Etrangère',
  'Permis',
];

const civiliteOptions = ['Homme', 'Femme'];

const ancienneteOptions = [
  { value: '', label: 'Tout' },
  { value: '0', label: 'Nouveau' },
  { value: '1', label: '3 mois à 1 an' },
  { value: '2', label: '1 an à 3 ans' },
  { value: '3', label: '3 ans à 6 ans' },
  { value: '4', label: '+ 6 ans' },
];

const typeContratOptions = [
  'CDI',
  'CDD',
  'CTT (Intérim)',
  'Intermitent',
  'Saisonier',
  'Retraité',
  'Retraité militaire',
  'Militaire',
  'Gérant',
  'Autre',
];

const INITIAL_PARAMS = {
  typesPersonne: { Noir: false, Arabe: false },
  domiciliation: [],
  saveSearch: false,
  quatre_bords_visibles: false,
};

const AccordionHeader = ({ icon: Icon, label, count }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    {Icon && <Icon fontSize="small" />}
    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
      {label}
    </Typography>
    {count > 0 && (
      <Chip
        label={count}
        size="small"
        color="primary"
        sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
      />
    )}
  </Box>
);

const AdvancedSearch = ({ onSearch, onReset, countries }) => {
  const [accordionState, setAccordionState] = useState({
    identite: false,
    emploi: false,
    fiscal: false,
  });

  const [searchParams, setSearchParams] = useState(INITIAL_PARAMS);

  const domiciliationOptions = Array.from({ length: 99 }, (_, i) => i + 1);

  useEffect(() => {
    const saved = localStorage.getItem('searchParams');
    if (saved) {
      try {
        setSearchParams(JSON.parse(saved));
      } catch (_) {}
    }
  }, []);

  const toggleAccordion = (panel) => {
    setAccordionState((prev) => ({ ...prev, [panel]: !prev[panel] }));
  };

  const identiteCount =
    (searchParams.typeDoc ? 1 : 0) +
    (searchParams.country ? 1 : 0) +
    (searchParams.civilite ? 1 : 0) +
    (searchParams.birthYearFrom || searchParams.birthYearTo ? 1 : 0) +
    (searchParams.typesPersonne?.Noir || searchParams.typesPersonne?.Arabe ? 1 : 0) +
    (searchParams.pdf_identity !== undefined ? 1 : 0) +
    (searchParams.quatre_bords_visibles ? 1 : 0);

  const emploiCount =
    (searchParams.anciennete ? 1 : 0) +
    (searchParams.typeContrat ? 1 : 0) +
    (searchParams.domiciliation?.length > 0 ? 1 : 0) +
    (searchParams.hasRib ? 1 : 0) +
    (searchParams.hasQRCode ? 1 : 0) +
    (searchParams.pay_pdf !== undefined ? 1 : 0);

  const fiscalCount =
    (searchParams.revenueFrom || searchParams.revenueTo ? 1 : 0) +
    (searchParams.partsCount ? 1 : 0) +
    (searchParams.pdf_impots !== undefined ? 1 : 0);

  const handleSearch = () => {
    if (searchParams.saveSearch) {
      localStorage.setItem('searchParams', JSON.stringify(searchParams));
    }
    onSearch(searchParams);
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setSearchParams((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectAll = (event) => {
    setSearchParams((prev) => ({
      ...prev,
      domiciliation: event.target.checked ? domiciliationOptions.slice() : [],
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    const [field, type] = name.split('-');
    if (field === 'typesPersonne') {
      setSearchParams((prev) => ({
        ...prev,
        typesPersonne: { ...prev.typesPersonne, [type]: checked },
      }));
    } else {
      setSearchParams((prev) => ({ ...prev, [name]: checked }));
    }
  };

  const handleReset = () => {
    setSearchParams({
      ...INITIAL_PARAMS,
      typesPersonne: { Noir: false, Arabe: false },
      domiciliation: [],
      pdf_identity: undefined,
      pdf_impots: undefined,
      pay_pdf: undefined,
    });
    localStorage.removeItem('searchParams');
    onReset();
  };

  const accordionSx = {
    mb: 1,
    borderRadius: 1,
    boxShadow: 2,
    '&:before': { display: 'none' },
    '&.Mui-expanded': { mt: 0 },
  };

  const summarySx = {
    backgroundColor: 'grey.100',
    borderRadius: 1,
    minHeight: 48,
    '&.Mui-expanded': { minHeight: 48 },
  };

  return (
    <Paper elevation={3} sx={{ p: 2, mt: 2 }}>
      <Typography
        variant="subtitle1"
        sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold', mb: 1, color: 'primary.main' }}
      >
        <SearchIcon fontSize="small" />
        Recherche
      </Typography>
      {/* 🪪 Identité */}
      <Accordion
        expanded={accordionState.identite}
        onChange={() => toggleAccordion('identite')}
        sx={accordionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
          <AccordionHeader icon={BadgeIcon} label="Identité" count={identiteCount} />
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Type de document
              </Typography>
              <Select
                name="typeDoc"
                value={searchParams.typeDoc || ''}
                onChange={handleSelectChange}
                displayEmpty
                fullWidth
                size="small"
              >
                <MenuItem value="">
                  <em>Tous</em>
                </MenuItem>
                {typeDocOptions.map((o) => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Pays
              </Typography>
              <Select
                name="country"
                value={searchParams.country || ''}
                onChange={handleSelectChange}
                displayEmpty
                fullWidth
                size="small"
              >
                <MenuItem value="">
                  <em>Tous</em>
                </MenuItem>
                {countries.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Civilité
              </Typography>
              <Select
                name="civilite"
                value={searchParams.civilite || ''}
                onChange={handleSelectChange}
                displayEmpty
                fullWidth
                size="small"
              >
                <MenuItem value="">
                  <em>Tous</em>
                </MenuItem>
                {civiliteOptions.map((o) => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Année de naissance
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <TextField
                    name="birthYearFrom"
                    type="number"
                    label="De"
                    value={searchParams.birthYearFrom || ''}
                    onChange={handleSelectChange}
                    fullWidth
                    size="small"
                    placeholder="Ex: 1980"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    name="birthYearTo"
                    type="number"
                    label="À"
                    value={searchParams.birthYearTo || ''}
                    onChange={handleSelectChange}
                    fullWidth
                    size="small"
                    placeholder="Ex: 2000"
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Type de personne
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    name="typesPersonne-Noir"
                    checked={searchParams.typesPersonne.Noir}
                    onChange={handleCheckboxChange}
                  />
                }
                label="Black"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="typesPersonne-Arabe"
                    checked={searchParams.typesPersonne.Arabe}
                    onChange={handleCheckboxChange}
                  />
                }
                label="Maghrébin"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Doc identité
              </Typography>
              <RadioGroup
                row
                value={
                  searchParams.pdf_identity === true
                    ? 'scanned'
                    : searchParams.pdf_identity === false
                    ? 'photo'
                    : 'all'
                }
                onChange={(e) => {
                  const val = e.target.value;
                  setSearchParams((prev) => ({
                    ...prev,
                    pdf_identity: val === 'all' ? undefined : val === 'scanned',
                  }));
                }}
              >
                <FormControlLabel value="all" control={<Radio size="small" />} label="Tous" />
                <FormControlLabel value="scanned" control={<Radio size="small" />} label="Scanné" />
                <FormControlLabel value="photo" control={<Radio size="small" />} label="Photo" />
              </RadioGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    name="quatre_bords_visibles"
                    checked={searchParams.quatre_bords_visibles || false}
                    onChange={handleCheckboxChange}
                  />
                }
                label="4 bords visibles"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* 💼 Emploi */}
      <Accordion
        expanded={accordionState.emploi}
        onChange={() => toggleAccordion('emploi')}
        sx={accordionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
          <AccordionHeader icon={WorkIcon} label="Emploi" count={emploiCount} />
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Ancienneté du poste
              </Typography>
              <Select
                name="anciennete"
                value={searchParams.anciennete || ''}
                onChange={handleSelectChange}
                displayEmpty
                fullWidth
                size="small"
              >
                {ancienneteOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Type de contrat
              </Typography>
              <Select
                name="typeContrat"
                value={searchParams.typeContrat || ''}
                onChange={handleSelectChange}
                displayEmpty
                fullWidth
                size="small"
              >
                <MenuItem value="">
                  <em>Tous</em>
                </MenuItem>
                {typeContratOptions.map((o) => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Domiciliation
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel id="domiciliation-label">Sélectionnez</InputLabel>
                <Select
                  labelId="domiciliation-label"
                  name="domiciliation"
                  multiple
                  value={searchParams.domiciliation || []}
                  onChange={(e) => handleSelectChange(e)}
                  renderValue={(selected) => selected.join(', ')}
                  label="Sélectionnez"
                >
                  <MenuItem value="">
                    <Checkbox
                      checked={searchParams.domiciliation.length === domiciliationOptions.length}
                      onChange={handleSelectAll}
                    />
                    <ListItemText primary="Tout cocher" />
                  </MenuItem>
                  {domiciliationOptions.map((o) => (
                    <MenuItem key={o} value={o}>
                      <Checkbox checked={searchParams.domiciliation.indexOf(o) > -1} />
                      <ListItemText primary={o} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Présence RIB sur FDP
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    name="hasRib"
                    checked={searchParams.hasRib || false}
                    onChange={handleCheckboxChange}
                  />
                }
                label="Oui"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Présence QRCode sur FDP
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    name="hasQRCode"
                    checked={searchParams.hasQRCode || false}
                    onChange={handleCheckboxChange}
                  />
                }
                label="Oui"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Bulletins de salaire
              </Typography>
              <RadioGroup
                row
                value={searchParams.pay_pdf === true ? 'pdf' : 'all'}
                onChange={(e) => {
                  const val = e.target.value;
                  setSearchParams((prev) => ({
                    ...prev,
                    pay_pdf: val === 'all' ? undefined : true,
                  }));
                }}
              >
                <FormControlLabel value="all" control={<Radio size="small" />} label="Tous" />
                <FormControlLabel value="pdf" control={<Radio size="small" />} label="PDF" />
              </RadioGroup>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* 💰 Fiscal */}
      <Accordion
        expanded={accordionState.fiscal}
        onChange={() => toggleAccordion('fiscal')}
        sx={accordionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
          <AccordionHeader icon={AccountBalanceIcon} label="Fiscal" count={fiscalCount} />
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Revenu imposable
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <TextField
                    name="revenueFrom"
                    type="number"
                    label="De"
                    value={searchParams.revenueFrom || ''}
                    onChange={handleSelectChange}
                    fullWidth
                    size="small"
                    placeholder="Ex: 10000"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    name="revenueTo"
                    type="number"
                    label="À"
                    value={searchParams.revenueTo || ''}
                    onChange={handleSelectChange}
                    fullWidth
                    size="small"
                    placeholder="Ex: 50000"
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Nombre de parts fiscales
              </Typography>
              <TextField
                name="partsCount"
                type="number"
                value={searchParams.partsCount || ''}
                onChange={handleSelectChange}
                fullWidth
                size="small"
                placeholder="Ex: 2"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Imposition
              </Typography>
              <RadioGroup
                row
                value={searchParams.pdf_impots === true ? 'pdf' : 'all'}
                onChange={(e) => {
                  const val = e.target.value;
                  setSearchParams((prev) => ({
                    ...prev,
                    pdf_impots: val === 'all' ? undefined : true,
                  }));
                }}
              >
                <FormControlLabel value="all" control={<Radio size="small" />} label="Tous" />
                <FormControlLabel value="pdf" control={<Radio size="small" />} label="PDF" />
              </RadioGroup>
            </Grid>

          </Grid>
        </AccordionDetails>
      </Accordion>

      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              name="saveSearch"
              checked={searchParams.saveSearch}
              onChange={handleCheckboxChange}
            />
          }
          label="Sauvegarder ma recherche"
        />
        <Box>
          <Button variant="outlined" color="secondary" onClick={handleReset} sx={{ mr: 2 }}>
            Réinitialiser
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            disabled={Object.keys(searchParams).length === 0}
          >
            Rechercher
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default AdvancedSearch;
